/**
 * Column layouts for the "Your data" CSV exports. Pure: the hook in
 * src/hooks/useDataExport.ts fetches the rows, these turn them into CSV.
 */
import { isoDate, isoDateTime, isoTime, toCsvFile, type CsvColumn } from './csv';

export type ExportDataset = 'customers' | 'bookings' | 'enquiries' | 'invoices' | 'services';

export interface ExportCustomerRow {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  created_at: string | null;
}

export interface ExportJobRow {
  id: string;
  title: string | null;
  status: string | null;
  scheduled_date: string | null;
  start_time: string | null;
  end_time: string | null;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  address: string | null;
  service_id: string | null;
  team_member_id: string | null;
  customer_id: string | null;
  required_cleaners: number | null;
  customer_notes: string | null;
  internal_notes: string | null;
  notes: string | null;
  created_at: string | null;
}

export interface ExportAssignmentRow {
  job_id: string;
  team_member_id: string;
}

export interface ExportEnquiryRow {
  id: string;
  created_at: string | null;
  status: string | null;
  subject: string | null;
  client_name: string | null;
  client_email: string | null;
  client_phone: string | null;
  address: string | null;
  service_id: string | null;
  frequency: string | null;
  premises_size: string | null;
  additional_info: string | null;
  customer_id: string | null;
  converted_booking_id: string | null;
}

export interface ExportInvoiceRow {
  id: string;
  invoice_number: string | null;
  status: string | null;
  issued_at: string | null;
  due_date: string | null;
  customer_name: string | null;
  customer_email: string | null;
  customer_id: string | null;
  booking_id: string | null;
  currency_code: string | null;
  subtotal_amount: number | string | null;
  tax_amount: number | string | null;
  total_amount: number | string | null;
  deposit_amount: number | string | null;
  amount_paid: number | string | null;
  amount_refunded: number | string | null;
  notes: string | null;
  created_at: string | null;
}

export interface ExportServiceRow {
  id: string;
  name: string | null;
  category: string | null;
  description: string | null;
  price: number | string | null;
  duration: string | null;
  is_active: boolean | null;
  is_quote_only: boolean | null;
  created_at: string | null;
}

export interface ExportLookups {
  serviceNames: Map<string, string>;
  teamNames: Map<string, string>;
  /** job id -> team member ids assigned to it */
  assignments: Map<string, string[]>;
}

const money = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
};

const OPEN_INVOICE_STATUSES = new Set(['sent', 'partially_paid', 'overdue']);

/** What's still owed on an invoice: total less net payments, for open invoices only. */
export function invoiceOutstanding(row: Pick<ExportInvoiceRow, 'status' | 'total_amount' | 'amount_paid' | 'amount_refunded'>): number {
  if (!OPEN_INVOICE_STATUSES.has(String(row.status ?? ''))) return 0;
  const owed = (money(row.total_amount) ?? 0) - ((money(row.amount_paid) ?? 0) - (money(row.amount_refunded) ?? 0));
  return Math.max(0, Math.round(owed * 100) / 100);
}

/** 'Paid' / 'Outstanding' / 'Part paid' / status label for the invoice CSV. */
export function invoicePaymentState(row: ExportInvoiceRow): string {
  const status = String(row.status ?? '');
  if (status === 'paid') return 'Paid';
  if (status === 'refunded') return 'Refunded';
  if (status === 'void' || status === 'cancelled') return 'Cancelled';
  if (status === 'draft') return 'Draft';
  const outstanding = invoiceOutstanding(row);
  if (outstanding <= 0) return 'Paid';
  return (money(row.amount_paid) ?? 0) > 0 ? 'Part paid' : 'Outstanding';
}

/** Postgres interval text ("01:30:00", "2 hours") as whole minutes when possible. */
export function intervalMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const hms = /^(?:(\d+) days? )?(\d{1,3}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (hms) return Number(hms[1] ?? 0) * 1440 + Number(hms[2]) * 60 + Number(hms[3]);
  return null;
}

export const customerColumns: CsvColumn<ExportCustomerRow>[] = [
  { header: 'Name', value: (r) => r.name },
  { header: 'Email', value: (r) => r.email },
  { header: 'Phone', value: (r) => r.phone },
  { header: 'Address', value: (r) => r.address },
  { header: 'Customer since', value: (r) => isoDateTime(r.created_at) },
  { header: 'Customer ID', value: (r) => r.id },
];

export function bookingColumns(lookups: ExportLookups): CsvColumn<ExportJobRow>[] {
  const cleaners = (job: ExportJobRow) => {
    const ids = new Set(lookups.assignments.get(job.id) ?? []);
    if (job.team_member_id) ids.add(job.team_member_id);
    return [...ids]
      .map((id) => lookups.teamNames.get(id) ?? 'Former team member')
      .sort((a, b) => a.localeCompare(b))
      .join('; ');
  };
  return [
    { header: 'Date', value: (r) => isoDate(r.scheduled_date) },
    { header: 'Start', value: (r) => isoTime(r.start_time) },
    { header: 'End', value: (r) => isoTime(r.end_time) },
    { header: 'Title', value: (r) => r.title },
    { header: 'Service', value: (r) => (r.service_id ? lookups.serviceNames.get(r.service_id) ?? '' : '') },
    { header: 'Status', value: (r) => r.status },
    { header: 'Assigned cleaners', value: cleaners },
    { header: 'Cleaners needed', value: (r) => r.required_cleaners },
    { header: 'Customer name', value: (r) => r.client_name },
    { header: 'Customer email', value: (r) => r.client_email },
    { header: 'Customer phone', value: (r) => r.client_phone },
    { header: 'Address', value: (r) => r.address },
    { header: 'Customer notes', value: (r) => r.customer_notes },
    { header: 'Internal notes', value: (r) => r.internal_notes },
    { header: 'Notes', value: (r) => r.notes },
    { header: 'Created', value: (r) => isoDateTime(r.created_at) },
    { header: 'Booking ID', value: (r) => r.id },
    { header: 'Customer ID', value: (r) => r.customer_id },
  ];
}

export function enquiryColumns(lookups: Pick<ExportLookups, 'serviceNames'>): CsvColumn<ExportEnquiryRow>[] {
  return [
    { header: 'Received', value: (r) => isoDateTime(r.created_at) },
    { header: 'Status', value: (r) => r.status },
    { header: 'Subject', value: (r) => r.subject },
    { header: 'Service', value: (r) => (r.service_id ? lookups.serviceNames.get(r.service_id) ?? '' : '') },
    { header: 'Name', value: (r) => r.client_name },
    { header: 'Email', value: (r) => r.client_email },
    { header: 'Phone', value: (r) => r.client_phone },
    { header: 'Address', value: (r) => r.address },
    { header: 'Frequency', value: (r) => r.frequency },
    { header: 'Premises size', value: (r) => r.premises_size },
    { header: 'Details', value: (r) => r.additional_info },
    { header: 'Enquiry ID', value: (r) => r.id },
    { header: 'Customer ID', value: (r) => r.customer_id },
    { header: 'Booking ID', value: (r) => r.converted_booking_id },
  ];
}

export const invoiceColumns: CsvColumn<ExportInvoiceRow>[] = [
  { header: 'Invoice number', value: (r) => r.invoice_number },
  { header: 'Status', value: (r) => r.status },
  { header: 'Payment', value: invoicePaymentState },
  { header: 'Issued', value: (r) => isoDate(r.issued_at) },
  { header: 'Due', value: (r) => isoDate(r.due_date) },
  { header: 'Customer name', value: (r) => r.customer_name },
  { header: 'Customer email', value: (r) => r.customer_email },
  { header: 'Currency', value: (r) => r.currency_code },
  { header: 'Subtotal', value: (r) => money(r.subtotal_amount) },
  { header: 'Tax', value: (r) => money(r.tax_amount) },
  { header: 'Total', value: (r) => money(r.total_amount) },
  { header: 'Deposit', value: (r) => money(r.deposit_amount) },
  { header: 'Paid', value: (r) => money(r.amount_paid) },
  { header: 'Refunded', value: (r) => money(r.amount_refunded) },
  { header: 'Outstanding', value: invoiceOutstanding },
  { header: 'Notes', value: (r) => r.notes },
  { header: 'Created', value: (r) => isoDateTime(r.created_at) },
  { header: 'Invoice ID', value: (r) => r.id },
  { header: 'Customer ID', value: (r) => r.customer_id },
  { header: 'Booking ID', value: (r) => r.booking_id },
];

export const serviceColumns: CsvColumn<ExportServiceRow>[] = [
  { header: 'Name', value: (r) => r.name },
  { header: 'Category', value: (r) => r.category },
  { header: 'Description', value: (r) => r.description },
  { header: 'Price', value: (r) => money(r.price) },
  { header: 'Duration (minutes)', value: (r) => intervalMinutes(r.duration) },
  { header: 'Quote only', value: (r) => Boolean(r.is_quote_only) },
  { header: 'Active', value: (r) => r.is_active !== false },
  { header: 'Created', value: (r) => isoDateTime(r.created_at) },
  { header: 'Service ID', value: (r) => r.id },
];

/** File name like `customers-my-cleaning-co-2026-11-01.csv`. */
export function exportFileName(dataset: string, orgName: string | null | undefined, ext = 'csv', now = new Date()): string {
  const slug = String(orgName ?? 'business')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'business';
  const day = now.toISOString().slice(0, 10);
  return `${dataset}-${slug}-${day}.${ext}`;
}

export { toCsvFile };
